/* eslint-disable max-statements */
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  SocialsAiInstruction,
  SocialsAiInstructionUpdateInput
} from "@bluframe/mightychroma-mightytool-frontend-types"
import { FormEventHandler, RefObject, useRef, useState } from "react"

import Item from "./Item"

type ModalContent = "deleteConfirm" | "saveConfirm" | null

export interface Props {
  isAdd?: boolean
  isEditing?: boolean
  isSaveLoading: boolean
  item?: SocialsAiInstruction
  onCancel: () => void
  onDelete?: () => void
  onEdit?: () => void

  onSave: (contest: Omit<SocialsAiInstructionUpdateInput, "id">) => void
}

export interface ComponentProps extends Omit<
  Props,
  "isEditing" | "onCancel" | "onDelete" | "onEdit" | "onSave"
> {
  actionButtonLabel: string
  actionButtonOnClick?: () => void
  areActionsDisabled: boolean
  contentRef: RefObject<HTMLTextAreaElement | null>
  descriptionRef: RefObject<HTMLTextAreaElement | null>
  identifierRef: RefObject<HTMLInputElement | null>
  isEditMode: boolean
  isModalOpen: boolean
  modalContent: ModalContent
  nameRef: RefObject<HTMLInputElement | null>
  onModalClose: () => void
  onModalDeleteConfirm: () => void
  onModalSaveConfirm: () => void
  onSubmit: FormEventHandler
  orderRef: RefObject<HTMLInputElement | null>
  outlinedButtonLabel: string
  outlinedButtonOnClick?: () => void
}

const DEFAULT_MODAL_CONTENT = null

export const usePrepareComponent = ({
  isEditing,
  onCancel,
  onDelete,
  onEdit,
  onSave,
  ...props
}: Props): ComponentProps => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState<ModalContent>(
    DEFAULT_MODAL_CONTENT
  )

  const contentRef = useRef<HTMLTextAreaElement>(null)
  const descriptionRef = useRef<HTMLTextAreaElement>(null)
  const identifierRef = useRef<HTMLInputElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const orderRef = useRef<HTMLInputElement>(null)

  const isEditMode = Boolean(props.isAdd || isEditing)

  const onModalClose = () => {
    setModalContent(DEFAULT_MODAL_CONTENT)
    setIsModalOpen(false)
  }

  const onModalConfirmOpen = (newModalContent: ModalContent) => () => {
    setModalContent(newModalContent)
    setIsModalOpen(true)
  }

  const onModalDeleteConfirm = () => {
    onDelete?.()
    onModalClose()
  }

  const onModalSaveConfirm = () => {
    onModalClose()

    if (!nameRef.current) {
      return
    }

    onSave({
      content: contentRef.current?.value ?? "",
      description: descriptionRef.current?.value,
      identifier: identifierRef.current?.value ?? "",
      name: nameRef.current.value,
      order: orderRef.current?.value
        ? Number(orderRef.current?.value)
        : undefined
    })
  }

  const onSubmit: FormEventHandler = (event) => {
    event.preventDefault()

    if (!nameRef.current) {
      return
    }

    onModalConfirmOpen("saveConfirm")()
  }

  const actionButtonLabel = isEditMode ? "Save" : "Delete"
  const outlinedButtonLabel = isEditMode ? "Cancel" : "Edit"

  const actionButtonOnClick = isEditMode
    ? undefined
    : onModalConfirmOpen("deleteConfirm")
  const outlinedButtonOnClick = isEditMode ? onCancel : onEdit

  const areActionsDisabled = props.isSaveLoading

  return {
    ...props,
    actionButtonLabel,
    actionButtonOnClick,
    areActionsDisabled,
    contentRef,
    descriptionRef,
    identifierRef,
    isEditMode,
    isModalOpen,
    modalContent,
    nameRef,
    onModalClose,
    onModalDeleteConfirm,
    onModalSaveConfirm,
    onSubmit,
    orderRef,
    outlinedButtonLabel,
    outlinedButtonOnClick
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Item
)
